import React, { useMemo } from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import type { PriceValuePropAsset } from '../../../propValues.types';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { galleryDesktopAspect, galleryMobileAspect, priceValuePropBackground } from '../../CardsConfig';
import { useStationeryTemplateProductPrices } from '../hooks/useStationeryTemplateProductPrices';
import { useProductOfferings } from '../../../../ConciergeServiceSection/useProductOfferings';
import { priceStringToMinorUnits, useCardDiscountContext } from '../../../useCardDiscountContext';
import { useCardCategoryContext } from '../../../../CardCategoryProvider';
import { useStationeryTemplateCategoryTranslations } from '@apps/card/hooks/useStationeryTemplateCategoryTranslations';
import { isConciergeServiceCategory } from '@apps/card/routes/Concierge/config';
import type { ConciergeServiceCategory } from '@apps/card/routes/Concierge/config';
import { useTranslation } from '@shared/core';
import { usePrintCurrency } from '@apps/card/hooks/usePrintCurrency';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import { CurrencyCode } from '@shared/hooks/useGeoLocale';

type PriceValuePropCardProps = {
  data: PriceValuePropAsset & { type: 'priceValueProp' };
};

const PriceRow = ({ label, price, strikethroughPrice }: { label: string; price: string; strikethroughPrice?: string }) => (
  <Flex
    flexDirection="row"
    alignItems="flex-end"
    gap={{ _: '4px', md: '6px' }}
  >
    <Box>
      <TextV2
        as="span"
        fontSize={{ _: '11px', md: '15px' }}
        fontWeight="regular"
        lineHeight="1.4"
        color="white"
      >
        {label}
        {strikethroughPrice !== undefined ? ' ' : ''}
      </TextV2>
      {strikethroughPrice !== undefined && (
        <TextV2
          as="span"
          fontSize={{ _: '11px', md: '15px' }}
          fontWeight="regular"
          lineHeight="1.4"
          color="white"
          textDecoration="line-through"
        >
          {strikethroughPrice}
        </TextV2>
      )}
    </Box>
    <TextV2
      typographyVariant="hed2"
      fontSize={{ _: '13px', md: '17px' }}
      color="white"
    >
      {price}
      {strikethroughPrice !== undefined && '*'}
    </TextV2>
  </Flex>
);

export const PriceValuePropCard = ({ data }: PriceValuePropCardProps) => {
  const isMobile = useIsMobileScreen();
  const { tq } = useTranslation('stationery');

  const productPrices = useStationeryTemplateProductPrices(data.representativeTemplate);
  // Digital before Paper — hook returns a fresh array each render so no memo needed
  const sortedProductPrices = [...productPrices].sort((a, b) => {
    const isDigitalA = a.productType === 'digitalCard' || a.productType === 'digital';
    const isDigitalB = b.productType === 'digitalCard' || b.productType === 'digital';
    return isDigitalA === isDigitalB ? 0 : isDigitalA ? -1 : 1;
  });

  const { currentCategory } = useCardCategoryContext();
  // Headline noun tracks the category being browsed. Of the `stationeryTemplateCategory`
  // key sets this is the only one that is plural for every category *and* has a non-empty
  // `default` — `stationeryTemplateCategoryToTile` is singular for saveTheDate/invitation,
  // and `dashboardCategoryNav` defaults to an empty string.
  const getCategoryNoun = useStationeryTemplateCategoryTranslations('designsGalleryPdpBreadcrumbCategory');
  const categoryNoun = getCategoryNoun(currentCategory) || tq`Cards`;
  const { productOfferings } = useProductOfferings();
  const cardDiscountContext = useCardDiscountContext();
  const { currency } = usePrintCurrency();
  const [isLocalCurrencyEnabled] = useIsFeatureEnabled('printLocalCurrency');
  const showUsdLabel = isLocalCurrencyEnabled && currency !== 'USD';
  const formatMinorUnits = useMinorUnitsToPriceString();

  const conciergeRow = useMemo(() => {
    if (!isConciergeServiceCategory(currentCategory)) return null;
    const offerings = productOfferings[currentCategory as ConciergeServiceCategory] ?? [];
    if (!offerings.length) return null;

    const cheapest = offerings.reduce((min, o) => (priceStringToMinorUnits(o.costPerHundredString) < priceStringToMinorUnits(min.costPerHundredString) ? o : min));
    const cheapestMinorUnits = priceStringToMinorUnits(cheapest.costPerHundredString);
    const discountResult = cardDiscountContext!.paperlust.getPrice(cheapestMinorUnits);

    // Concierge prices are hardcoded USD on the backend with no FX conversion, so we
    // always format with currency:'USD' to get a $X.XX string, then append ' USD' for
    // non-USD users. Using discountResult.discountedPrice directly would be wrong here
    // because useGetDiscountedPrice formats via useMinorUnitsToPriceString without a
    // currency override, producing e.g. "€4.74" when printLocalCurrency is on.
    const sale = 'currentSale' in discountResult ? discountResult.currentSale : undefined;
    const withUsd = (price: string) => (showUsdLabel ? `${price} USD` : price);
    const formatConciergePrice = (minorUnits: number) => withUsd(formatMinorUnits(minorUnits, { currency: CurrencyCode.USD }));

    const discountedMinorUnits = sale ? Math.round(cheapestMinorUnits * (1 - sale.discountPercentage)) : null;

    return {
      basePrice: formatConciergePrice(cheapestMinorUnits),
      discountedPrice: discountedMinorUnits !== null ? formatConciergePrice(discountedMinorUnits) : undefined,
      preDiscountPrice: discountedMinorUnits !== null ? formatConciergePrice(cheapestMinorUnits) : undefined,
      promoCode: sale?.code,
    };
  }, [currentCategory, productOfferings, cardDiscountContext, showUsdLabel, formatMinorUnits]);

  const promoCode = sortedProductPrices.find(p => p.discount)?.discount?.promoCode ?? conciergeRow?.promoCode;
  const hasAnyDiscount = sortedProductPrices.some(p => !!p.discount) || !!conciergeRow?.discountedPrice;

  return (
    <Flex
      gridArea="auto"
      aspectRatio={isMobile ? galleryMobileAspect : galleryDesktopAspect}
      flexDirection="column"
      background={priceValuePropBackground}
      width="100%"
      height="100%"
      overflow="hidden"
      paddingTop={{ _: 8, md: '50px' }}
      paddingX={{ _: 5, md: 7 }}
    >
      <TextV2
        fontSize={{ _: '30px', md: '44px' }}
        fontWeight="semibold"
        lineHeight="1.2"
        letterSpacing="-0.015em"
        color="white"
      >
        {tq`${categoryNoun} for every budget.`}
      </TextV2>

      <Flex
        flexDirection="column"
        gap="4px"
        marginTop={{ _: 7, md: '36px' }}
      >
        {sortedProductPrices.map(product => {
          const hasDiscount = !!product.discount && product.preDiscountedPrice !== product.basePrice;
          return (
            <PriceRow
              key={product.productId}
              label={product.name}
              price={product.basePrice}
              strikethroughPrice={hasDiscount ? product.preDiscountedPrice : undefined}
            />
          );
        })}

        {conciergeRow && (
          <PriceRow
            label={tq`Concierge`}
            price={conciergeRow.discountedPrice ?? conciergeRow.basePrice}
            strikethroughPrice={conciergeRow.discountedPrice ? conciergeRow.preDiscountPrice : undefined}
          />
        )}
      </Flex>

      {hasAnyDiscount && promoCode && (
        <TextV2
          typographyVariant="hed1"
          fontSize={{ _: '11px', md: '15px' }}
          color="white"
          marginTop={{ _: 7, md: '38px' }}
        >
          {tq`*Use code: ${promoCode}`}
        </TextV2>
      )}
    </Flex>
  );
};
